import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { EntityForm } from "@/components/v2/entity-form";
import { EmptyState } from "@/components/v2/empty-state";
import { useCrew, useUpdateCrew } from "@/hooks/use-api";
import {
  CREW_FIELDS,
  crewToFormValues,
  type CrewFormValues,
} from "./crew-form-config";

export default function V2CrewEditPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const crewQuery = useCrew(id);
  const updateCrew = useUpdateCrew();
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(values: CrewFormValues) {
    setError(null);
    try {
      await updateCrew.mutateAsync({
        id,
        input: values as Record<string, unknown>,
      });
      toast.success("Crew updated");
      navigate("/crews");
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Could not save your changes — check the fields and try again.",
      );
    }
  }

  const crew = (
    crewQuery.data as { crew?: Record<string, unknown> } | undefined
  )?.crew;

  if (crewQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="size-6 animate-spin text-[color:var(--color-text-lo)]" />
      </div>
    );
  }

  if (crewQuery.isError || !crew) {
    return (
      <EmptyState
        title="Crew not found"
        description="This crew may have been removed, or you don't have access to it."
        action={{ label: "Back to crews", onClick: () => navigate("/crews") }}
      />
    );
  }

  return (
    <EntityForm<CrewFormValues>
      title="Edit crew"
      breadcrumbs={[{ label: "Crews", href: "/crews" }, { label: "Edit" }]}
      defaultValues={crewToFormValues(crew)}
      fields={CREW_FIELDS}
      submitLabel="Save changes"
      onSubmit={onSubmit}
      cancelHref="/crews"
      error={error}
      isSubmitting={updateCrew.isPending}
    />
  );
}
